import React, { useState, useMemo } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Col,
  Form,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane
} from 'reactstrap';
import TableContainer from '../../../../Components/Common/TableContainerReactTable';
import classnames from 'classnames';
import FormInput from '../common/FormInput';
import SelectInput from '../common/SelectInput';
import LoadingIndicator from 'Components/Loading/LoadingIndicator';
import { useLazyGetPriceListQuery, PriceListData, PriceListSearchParams } from 'features/common/pricelist';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { usePickPriceListForProductMutation } from 'features/products/redux/productApiSlice';
import { toast } from 'react-toastify';
import { formatSearchParams } from 'utils/searchUtils';

export interface PriceListSearchForm {
  searchField: string;
  searchValue: string;
}

interface PickPriceListModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect?: (priceLists: PriceListData[]) => void;
}

const defaultSearchForm: PriceListSearchForm = {
  searchField: 'Name',
  searchValue: ''
};

const searchFieldOptions = ['Name', 'Type', 'Status', 'Currency'];

// Validated selector - ensures product is selected before using this modal
const selectRequiredProduct = (state: RootState) => {
  const product = state.products.selectedProduct;
  if (!product) throw new Error('Product must be selected to use this modal');
  return product;
};

const PickPriceListModal: React.FC<PickPriceListModalProps> = ({
  isOpen,
  onClose,
  onSelect
}) => {
  const [searchForm, setSearchForm] = useState<PriceListSearchForm>(defaultSearchForm);
  const [selectedPriceLists, setSelectedPriceLists] = useState<PriceListData[]>([]);
  const [activeTab] = useState('1'); // Only one tab, but keep for extensibility
  const [searchParams, setSearchParams] = useState<PriceListSearchParams>({
    priceListName: ''
  });


  //Pick Product price list
  const [pickProductPriceList, { isLoading: isPickProductPriceListLoading }] = usePickPriceListForProductMutation();

  //Redux state - Get the selected product with validation
  const selectedProduct = useSelector(selectRequiredProduct);
  const productRowId = selectedProduct.rowId;
  const productId = selectedProduct.productId;


  const handleSearchFieldChange = (field: keyof PriceListSearchForm, value: string) => {
    setSearchForm(prev => ({ ...prev, [field]: value }));
  };

  // Redux query
  const [getPriceList, { data: priceListData, isLoading: isPriceListLoading }] = useLazyGetPriceListQuery();

  const handleSearch = async () => {
    try {
      // Use utility function to format search parameters with '%' default
      const formattedParams = formatSearchParams.generic(searchForm.searchValue, 'priceListName');
      const searchParams: PriceListSearchParams = {
        priceListName: formattedParams.priceListName
      };
      setSearchParams(searchParams);
      await getPriceList(searchParams);
      console.log(priceListData);
    } catch (error) {
      console.error('Search failed:', error);
    }
  };

  const handleClear = () => {
    setSearchForm(defaultSearchForm);
    setSelectedPriceLists([]);
  };

  const handleCheckboxChange = (priceList: PriceListData, isChecked: boolean) => {
    if (isChecked) {
      setSelectedPriceLists(prev => [...prev, priceList]);
    } else {
      setSelectedPriceLists(prev => prev.filter(p => p.rowId !== priceList.rowId));
    }
  };


  const handleSelect = async () => {
    console.log('selectedPriceLists', selectedPriceLists);

    //Check if price list is selected
    if (selectedPriceLists.length === 0) {
      toast.error('Please select at least one price list');
      return;
    }

    //Pick product price lists - send multiple requests for multiple selections
    try {
      const promises = selectedPriceLists.map(priceList => 
        pickProductPriceList({ 
          productRowId: productRowId, 
          body: { priceListId: priceList.rowId }
        })
      );

      const responses = await Promise.all(promises);
      console.log('responses', responses);

      // Check if all requests were successful
      const successfulCount = responses.filter(response => response.data?.status !== 0).length;
      const failedCount = selectedPriceLists.length - successfulCount;

      if (successfulCount > 0) {
        if (failedCount === 0) {
          // All successful
          toast.success(`Successfully added ${successfulCount} price list${successfulCount !== 1 ? 's' : ''} to product`);
        } else {
          // Some successful, some failed
          toast.warning(`Added ${successfulCount} price list${successfulCount !== 1 ? 's' : ''}, but ${failedCount} failed`);
        }
        // Notify parent component about the successful selections
        onSelect?.(selectedPriceLists);
      } else {
        // All failed
        toast.error('Failed to add any price lists to product');
      }
    } catch (error) {
      toast.error('Error adding price lists to product');
      console.error('Error picking product price lists:', error);
    }

    //Close modal
    onClose();
  };

  const handleCancel = () => {
    setSelectedPriceLists([]);
    onClose();
  };

  const handleClears = () => {
    setSelectedPriceLists([]);
  };

  const isPriceListSelected = (priceList: PriceListData | undefined) => {
    if (!priceList) return false;
    return selectedPriceLists.some(p => p.rowId === priceList.rowId);
  };

  //select all price list

  // const isAllSelected = () => {
  //   return priceListData?.data && priceListData.data.length > 0 &&
  //     selectedPriceLists.length === priceListData.data.length;
  // };

  // Define columns for the table
  const columns = useMemo(() => [
    {
      header: 'Pick',
      accessorKey: 'pick',
      enableColumnFilter: false,
      size: 50,
      cell: (cell: any) => (
        <div className="d-flex justify-content-start align-items-center">
          <input
            type="checkbox"
            checked={isPriceListSelected(cell.row.original)}
            onChange={(e) => handleCheckboxChange(cell.row.original, e.target.checked)}
            className="form-check-input"
          />
        </div>
      )
    },
    {
      header: 'Name',
      accessorKey: 'priceListName',
      enableColumnFilter: false,
      cell: (cell: any) => (
        <div>
          <span className="text-link">
            {cell.getValue()}
          </span>
        </div>
      )
    },
    {
      header: "Active",
      accessorKey: "activeFlag",
      enableColumnFilter: false,
      cell: (cell: any) => {
        const isActive = cell.getValue();
        return isActive ? (
          <div className="text-start ps-1">
            <i className="ri-checkbox-circle-line fs-17 align-middle"></i>
          </div>
        ) : null;
      },
    },
  ], [selectedPriceLists, priceListData]);

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="lg">
      <ModalHeader toggle={onClose}>Pick Price List</ModalHeader>
      <ModalBody>
        {/* Tab Navigation */}
        <Nav pills className="nav nav-tabs-custom arrow-navtabs nav-success mb-3">
          <NavItem>
            <NavLink
              style={{ cursor: 'pointer' }}
              className={classnames({ active: activeTab === '1' })}
              tabIndex={0}
              aria-label="Search Tab"
              onClick={() => { }}
              onKeyDown={() => { }}
            >
              Search
            </NavLink>
          </NavItem>
        </Nav>
        <TabContent activeTab={activeTab} className="text-muted">
          <TabPane tabId="1">
            <Form>
              <Row className="mb-2 justify-content-center align-items-center gap-2">
                <Col md={4}>
                  <SelectInput
                    formData={searchForm}
                    setFormData={setSearchForm}
                    label="Search"
                    id="searchField"
                    options={searchFieldOptions}
                  />
                </Col>
                <Col md={4}>
                  <FormInput
                    id="searchValue"
                    label="For value"
                    value={searchForm.searchValue}
                    onChange={(val) => handleSearchFieldChange('searchValue', val)}
                    type="text"
                  />
                </Col>
                <Col md={1} className='me-3'>
                  <button
                    type="button"
                    onClick={() => handleSearch()}
                    style={{ minWidth: 80 }}
                    className="btn btn-soft-primary btn-sm"
                  >
                    Search
                  </button>
                </Col>
                <Col md={1} className=''>
                  <button
                    type="button"
                    onClick={handleClear}
                    style={{ minWidth: 80 }}
                    className="btn btn-soft-warning btn-sm"
                  >
                    Clear
                  </button>
                </Col>
              </Row>

              {/* Price List Table */}
              <Row>
                <Col xs={12}>
                  {isPriceListLoading ? (
                    <LoadingIndicator
                      isLoading={isPriceListLoading}
                      isProcessing={false}
                      error={null}
                      title="Loading price lists from server..."
                      height="200px"
                    />
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={priceListData?.data || []}
                      isGlobalFilter={false}
                      customPageSize={10}
                      divClass="mb-0"
                      tableClass="table-centered align-middle table-nowrap mb-0"
                      theadClass="table-light"
                      thClass="px-3 py-2"
                      verticalLines={true}
                    />
                  )}
                </Col>
              </Row>
            </Form>
          </TabPane>
        </TabContent>
      </ModalBody>
      <ModalFooter className="d-flex justify-content-between">
        <div className="text-muted">
          {selectedPriceLists.length > 0 && (
            <span>{selectedPriceLists.length} price list{selectedPriceLists.length !== 1 ? 's' : ''} selected</span>
          )}
        </div>
        <div className="d-flex gap-2">
          <button
            onClick={handleClears}
            className="btn btn-soft-warning btn-sm"
            style={{ minWidth: 80 }}
          >
            Clear
          </button>
          <button
            onClick={handleSelect}
            className="btn btn-soft-success btn-sm"
            style={{ minWidth: 80 }}
            disabled={selectedPriceLists.length === 0}
          >
            Select
          </button>
          <button
            className="btn btn-soft-dark btn-sm"
            onClick={handleCancel}
            style={{ minWidth: 80 }}
          >
            Cancel
          </button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default PickPriceListModal;
